import { useMemo } from 'react'

import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { isString } from 'lodash-es'

import {
  assignmentCostsQuery,
  assignmentDistancesQuery,
  assignmentExecutionsQuery,
  assignmentPlansQuery,
  assignmentQuery,
  nextAssignmentQuery,
  previousAssignmentQuery,
} from '@/state/assignments/assignments'
import { type RegionAssignment } from '@/state/assignments/types'
import { regionQuery } from '@/state/regions/region'

export const useRegionAssignment = (): RegionAssignment | null => {
  const { assignmentId } = useParams({ from: '/assignments/$assignmentId' })

  const { data: assignmentSnapshot } = useSuspenseQuery(assignmentQuery(assignmentId))
  const { data: costsSnapshot } = useSuspenseQuery(assignmentCostsQuery(assignmentId))
  const { data: plansSnapshot } = useSuspenseQuery(assignmentPlansQuery(assignmentId))

  const { data: executionsSnapshot } = useQuery(assignmentExecutionsQuery(assignmentId))
  const { data: distancesSnapshot } = useQuery(assignmentDistancesQuery(assignmentId))

  const regionId = isString(assignmentSnapshot.data?.regionId) ? assignmentSnapshot.data?.regionId : undefined
  const { data: regionSnapshot } = useQuery(regionQuery(regionId))

  const { data: previousSnapshot } = useQuery(previousAssignmentQuery(regionId, assignmentSnapshot.data?.timestamp))
  const { data: nextSnapshot } = useQuery(nextAssignmentQuery(regionId, assignmentSnapshot.data?.timestamp))

  const assignmentData = assignmentSnapshot.data
  const costsData = costsSnapshot.data
  const plansData = plansSnapshot.data
  const regionData = regionSnapshot?.data

  return useMemo<RegionAssignment | null>(() => {
    if (!assignmentData || !costsData || !plansData || !regionData || !regionId) {
      return null
    }

    return {
      _id: assignmentId,
      assignment: assignmentData,
      costs: costsData,
      region: regionData,
      solver: plansData.find((p) => p._id === 'solver'),
      executions: executionsSnapshot?.data ?? [],
      distancesFetched: distancesSnapshot?.data ?? [],
      previousId: previousSnapshot?.data?.[0]?._id,
      nextId: nextSnapshot?.data?.[0]?._id,
      regionId,
      groupId: assignmentData.groupId,
      timestamp: assignmentData.timestamp,
      teams: assignmentData.teams,
      transportRequests: assignmentData.transportRequests,
      executePlan: assignmentData.executePlan,
    }
  }, [
    assignmentId,
    assignmentData,
    costsData,
    distancesSnapshot?.data,
    executionsSnapshot?.data,
    nextSnapshot?.data,
    plansData,
    previousSnapshot?.data,
    regionData,
    regionId,
  ])
}
